import React, {useCallback, useEffect, useState} from "react"
import css from "../AuthPage/AuthPage.module.scss"
import {AuthHeader} from "../../features/Auth/AuthHeader/AuthHeader"
import {useHistory} from "react-router-dom"
import {useChangeLanguageAPI} from "../../features/ChangeLanguage/useChangeLanguageAPI"
import {useSelector} from "react-redux"
import {changeLanguageSelectors} from "../../slices/changeLanguageSlice"
import {ChangeLanguage} from "../../features/ChangeLanguage/ChangeLanguage"
import {Box} from "scholastic-client-components/src/ui/Box/Box"
import {useTranslation} from "react-i18next"
import {Button} from "scholastic-client-components/src/ui/Button/Button"
import {Input} from "scholastic-client-components/src/ui/Input/Input"
import {useAuthThroughSSO} from "./useAuthThroughSSO"
import {APIStatus} from "scholastic-client-components/src/types/APITypes"
import {AuthSSOResponse} from "../../api/authAPI"
import {SSOErrorTemplate} from "../../features/SSO/SSOErrorTemplate/SSOErrorTemplate"
import {TSNWebsiteLink} from "../../features/TSNWebsiteLink/TSNWebsiteLink"
import styles from "./LoginWithSSO.module.scss"
import {emailValidationRules} from "scholastic-client-components/src/features/person"
import {composeValidators} from "scholastic-client-components/src/features/ReduxForm/validators"

export const LoginWithSSO: React.FC = () => {
    useChangeLanguageAPI()
    const locales = useSelector(changeLanguageSelectors.getData)?.locales

    const {t} = useTranslation(["auth_portal"])

    const [authError, setAuthError] = useState("")
    const [email, setEmail] = useState("")
    const [emailTouched, setEmailTouched] = useState(false)
    const changeEmail = (value) => {
        setEmailTouched(true)
        setEmail(value)
    }

    const [emailError, setEmailError] = useState<string | undefined>(undefined)
    useEffect(() => {
        setEmailError(composeValidators(emailValidationRules.use)(email))
    }, [email])

    const history = useHistory()

    const handleRedirectToLogin = () => {
        history.push("/")
    }

    const onAuth = (value: AuthSSOResponse) => {
        if (value.noUser) setEmailError(t`auth_portal:auth_page.sso_disabled.no_user`)
        else if (value.noSsoInDistrict)
            setAuthError(t`auth_portal:auth_page.sso_disabled.sso_not_allowed`)
        else if (value.ssoDisabled)
            setAuthError(t`auth_portal:auth_page.sso_disabled.contact_admin`)
        else {
            window.location.href = value.url
        }
    }
    const {status, auth} = useAuthThroughSSO(onAuth)

    const startLogin = useCallback(
        (event) => {
            event.preventDefault()
            if (emailError) return
            auth(email.trim())
        },
        [email, auth, emailError]
    )

    return (
        <>
            <div className={css.language}>
                <ChangeLanguage locales={locales} />
            </div>

            <Box
                flex={{justifyContent: "center", alignItems: "center", columnDirection: true}}
                propsStyles={{minHeight: "80vh"}}
            >
                {authError ? (
                    <SSOErrorTemplate
                        headerTitle={t("auth_portal:auth_page.sso_disabled.title")}
                        message={authError}
                        handleRedirectToLogin={handleRedirectToLogin}
                    />
                ) : (
                    <form className={styles.form} onSubmit={startLogin}>
                        <AuthHeader text={""} onLogoClick={handleRedirectToLogin} mb={1.875} />

                        <Input
                            label={t("person:fieldname.email")}
                            data-test={"email-input"}
                            onChange={(e) => changeEmail(e.target.value)}
                            value={email}
                            error={emailTouched ? emailError : undefined}
                            disabled={status === APIStatus.Loading}
                            validationDataTest={"email-error"}
                            required
                        />

                        <div style={{width: "15rem", marginTop: "1.5rem", marginBottom: "1rem"}}>
                            <Button
                                type="submit"
                                primary
                                width100percent
                                loader={status === APIStatus.Loading}
                                data-test={"login-button"}
                            >
                                {t("auth_portal:auth_page.login_button")}
                            </Button>
                        </div>

                        <Button
                            onClick={handleRedirectToLogin}
                            text
                            disabled={status === APIStatus.Loading}
                        >
                            {t("auth_portal:auth_page.back_to_login_button")}
                        </Button>
                    </form>
                )}
                <div className={css.tsn}>
                    <TSNWebsiteLink />
                </div>
            </Box>
        </>
    )
}
